/**
 * Engagement Module Interfaces
 * Shared types for quiz-task, image-task, and other engagement modules
 */

// Import existing master data interfaces
import type { Region } from '../models/master-data/region.interface';
import type { State } from '../models/master-data/state.interface';
import type { City } from '../models/master-data/city.interface';
import type { UserRole } from '../models/user-management/supervisor-split-assign.interface';

/**
 * Main Engagement Entity
 * Represents a quiz task or image task
 */
export interface Engagement {
  task_id: number; // API uses task_id instead of id
  engagement_type_id: number; // API uses engagement_type_id
  engagement_type_name?: string;
  engagement_name: string;
  engagement_description?: string;
  reward_type?: number;
  reward_type_name?: string;
  reward_points?: number;
  whitelist_type: number;
  whitelist_type_name?: string;
  zone_type?: number | null;
  zone_type_name?: string;
  zone_id?: string | null;
  role_type?: number;
  role_type_name?: string;
  start_date: string;
  end_date: string;
  status?: number;
  cd?: string; // Created date from API
  created_at?: string;
  updated_at?: string;
  portfolio_id?: number;
  portfolio?: string; // API uses 'portfolio' for name
  campaign_id?: number | null;

  // Quiz/Content & Learning specific fields
  course_description?: string; // HTML content for course description
  course_content?: string; // YouTube URL or content link
  quiz_configured?: number; // 1 = quiz is configured, 0 = not configured
  pre_expiry_date?: string; // Pre-expiry date for rewards
}

/**
 * Engagement Type
 * Used in dropdown for selecting engagement type
 */
export interface EngagementType {
  id: number;
  name: string;
  engagement_page: number;
}

/**
 * Quiz Question with Options
 * Used in quiz-task FormArray
 */
export interface QuizQuestion {
  question: string;
  answer: string;
  points: number;
  options: QuizOption[];
}

/**
 * Quiz Option
 * Each question can have up to 4 options
 */
export interface QuizOption {
  option: string;
}

/**
 * Quiz option for calculate-quiz-points / create-quiz-content-task APIs
 */
export interface QuizPointsOption {
  option_text: string;
  is_right_option: number;
}

/**
 * Quiz question for calculate-quiz-points / create-quiz-content-task APIs
 */
export interface CalculateQuizPointsQuestion {
  question: string;
  points: number;
  options: QuizPointsOption[];
}

/** POST body for calculate-quiz-points */
export interface CalculateQuizPointsRequest {
  questions: CalculateQuizPointsQuestion[];
  quiz_points_criteria?: number;
  program_id?: number;
}

/**
 * Response from calculate-quiz-points API
 */
export interface CalculateQuizPointsResponse {
  success: boolean;
  status: number;
  data: {
    total_points: number;
    question_count: number;
    message: string;
  };
}

/**
 * Whitelisted User
 * User assigned to an engagement
 */
export interface WhitelistUser {
  id: number;
  user_id: number;
  username: string; // API returns 'username', not 'user_name'
  mobile_number: string; // API returns 'mobile_number'
  email?: string;
  phone?: string;
  region_name?: string;
  state_name?: string;
  city_name?: string;
  status: number | null; // API can return null
  created_at?: string;
}

// =============================================================================
// API Request Interfaces
// =============================================================================

/**
 * Request: Get engagement types
 */
export interface GetEngagementTypesRequest {
  engagement_page: number; // 2 for quiz-task, 3 for image-task
}

/**
 * Request: Get engagements list with filters
 */
export interface GetEngagementsRequest {
  engagement_page?: number; // 2 for quiz-task, 3 for image-task
  program_id?: number;
  engagement_type?: number;
  portfolio_id?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Request: View engagement detail for editing
 * Used in edit mode to prepopulate form
 */
export interface ViewEngagementRequest {
  engagement_page: number; // 2 for quiz, 3 for image
  engagement_type_id: number;
  portfolio_id?: number;
  task_id: number;
}

/**
 * Request: Create/Update Quiz Engagement
 * Same endpoint for create and update, differentiated by isEdit flag
 */
export interface CreateEngagementContentRequest {
  portfolio_id: number;
  engagement_id: number; // engagement_type_id
  engagement_title: string;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  quiz_points_criteria: number; // reward_type
  questions: QuizQuestion[];
  isEdit: number; // 0 for create, 1 for update
  task_id?: number; // Present when updating
  course_type: number; // 4 for quiz
  audit_instructions: string;
  whitelist_type: number;
  zone_type?: number;
  role_type?: number;
}

/**
 * Request: Create/Update Quiz + Content Task (New API)
 * Used for Content & Learning module (engagement_page=3)
 */
export interface CreateQuizContentTaskRequest {
  program_id: string | number;
  portfolio_id: number;
  engagement_type_id: string | number;
  engagement_page: number; // 3 for content & learning
  engagement_title: string;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  course_type: string | number; // "3" for content
  course_description?: string; // HTML content
  course_content?: string; // URL (YouTube, etc.)
  quiz_configured: string | number; // "1" if quiz is configured
  quiz_points_criteria?: string | number; // reward type
  reward_points?: number | null;
  pre_expiry_date?: string; // YYYY-MM-DD format
  questions: QuizQuestion[];
  task_id?: string | number; // Present when updating
  isEdit: number; // 0 for create, 1 for update
  whitelist_type: string | number;
  zone_type?: string | number;
  role_type?: string | number;
}

/**
 * Request: Delete Question and Options
 */
export interface DeleteQuestionAndOptionsRequest {
  engagement_type_id: number;
  engagement_task_id: number;
  deletedQuestionId?: string | number;
  deletedOptionsId?: string | number;
}

/**
 * Request: Get whitelisted users
 */
export interface GetWhitelistUsersRequest {
  engagement_page: number; // 1 for image-task, 2 for quiz-task, 3 for content-learning
  task_id: number;
  search?: string;
  page?: number;
  pageSize?: number;
  // Note: program_id is automatically added by HTTP interceptor
  // Note: engagement_type is not required by the API
}

/**
 * Request: Whitelist users (zone-based or all) - Used by all engagement types
 */
export interface WhitelistEngagementRequest {
  task_id: number;
  engagement_type_id: number;
  program_id: number;
  whitelist_type: number; // 1=Excel, 2=Zone-based, 3=Enrollment-based
  zone_type?: number; // 0=All zones, 1=Specific zone (required if whitelist_type=2)
  zone_id?: number | number[] | null; // Single ID or array when multiple zones selected
  user_group_id?: number; // Required if whitelist_type=3
  enrollment_setting_id?: number; // Required if whitelist_type=3
}

/**
 * Request: Disable/toggle engagement user status
 */
export interface DisableEngagementUserRequest {
  engagement_id: number;
  id: number; // whitelist user id
  status: number; // 0 or 1
  engagement_page: number; // 1=Picture, 2=Quiz, 3=Content & Learning
  program_id: string | number;
}

/**
 * Request: Deactivate entire engagement
 */
export interface DeactivateEngagementRequest {
  engagement_id: string | number;
  id: number;
  engagement_page: number; // 1=Picture, 2=Quiz, 3=Content & Learning
  program_id: string | number;
}

/**
 * Request: Assign users to engagement (whitelist)
 */
export interface AssignUsersRequest {
  program_id: number;
  engagement_id: number;
  user_ids: number[];
  upload_file?: File; // For Excel upload
}

/**
 * Request: Disable user from engagement
 */
export interface DisableUserRequest {
  engagement_id: number;
  user_id: number;
}

/**
 * Request: Get zones (region/state/city)
 */
export interface GetZonesRequest {
  program_id?: number;
  region_id?: number;
  state_id?: number;
}

// =============================================================================
// API Response Interfaces
// =============================================================================

/**
 * Response: Get engagement types
 */
export interface GetEngagementTypesResponse {
  data: {
    engagementtypes: EngagementType[];
    message?: string;
  };
  success: boolean;
  status: number;
}

/**
 * Response: Get engagements list
 */
export interface GetEngagementsResponse {
  data: {
    data: Engagement[]; // API returns data.data array
    pagination: {
      totalCount: number;
      pageSize: number;
      page: number;
      pageCount: number;
    };
  };
  success: boolean;
  status: number;
}

/**
 * Response: View engagement detail for editing
 */
export interface ViewEngagementResponse {
  data: {
    engagement: {
      engagement_type_id: number;
      engagement_title: string;
      start_date: string;
      end_date: string;
      quiz_points_criteria: number;
      course_description?: string; // HTML content
      course_content?: string; // URL
      quiz_configured?: number; // 1 if configured
      reward_points?: number;
      pre_expiry_date?: string;
      whitelist_type: number;
      zone_type?: number;
      role_type?: number;
      questions?: Array<{
        question: string;
        answer: string;
        points: number;
        option: Array<{
          option: string;
        }>;
      }>;
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Create/Update engagement
 */
export interface CreateEngagementResponse {
  body: {
    data: {
      task_id: number;
      sub_tasks_id: string;
      has_subtask: number;
      engagement_type_id: number;
      engagement_type: string;
      message?: string;
    };
  };
  status: number;
}

/**
 * Response: Get whitelisted users
 */
export interface GetWhitelistUsersResponse {
  body: {
    data: {
      data: WhitelistUser[];
      full_data?: WhitelistUser[]; // For excel export
    };
  };
  status: number;
}

/**
 * Response: Whitelist engagement users (zone-based or all)
 */
export interface WhitelistEngagementResponse {
  body: {
    data: {
      message: string;
    };
  };
  status: number;
}

/**
 * Response: Disable/toggle engagement user status
 */
export interface DisableEngagementResponse {
  data: {
    message: string;
  };
  status: number;
}

/**
 * Response: Assign users (for Excel upload)
 */
export interface AssignUsersResponse {
  data: {
    result: {
      assigned_count: number;
      message: string;
      rejected_records?: any[]; // For Excel upload errors
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Disable user
 */
export interface DisableUserResponse {
  data: {
    result: {
      message: string;
    };
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get user roles
 */
export interface GetUserRolesResponse {
  success: boolean;
  status: number;
  data: UserRole[];
}

/**
 * Response: Get regions
 */
export interface GetRegionsResponse {
  data: {
    result: Region[];
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get states
 */
export interface GetStatesResponse {
  data: {
    result: State[];
    message?: string;
  };
  success: boolean;
}

/**
 * Response: Get cities
 */
export interface GetCitiesResponse {
  data: {
    result: City[];
    message?: string;
  };
  success: boolean;
}

// =============================================================================
// Image Task Specific Interfaces
// =============================================================================

/**
 * Image Task Template Instructions
 * Stored as JSON string in template_instructions field
 */
export interface ImageTaskTemplateInstructions {
  templete_type: number; // 1 = single image, 2 = double image (typo from API)
  single_image?: string; // URL for single image type
  templete_wrong_img?: string; // URL for wrong image (double type)
  temple_right_img?: string; // URL for right image (double type)
  image_insruction?: string; // Instruction text for single image
  image_insruction_wrong?: string; // Instruction for wrong image
  image_insruction_right?: string; // Instruction for right image
  templete_insruction_one?: string; // General instruction 1
  templete_insruction_two?: string; // General instruction 2
  templete_insruction_three?: string; // General instruction 3
  templete_title?: string; // Template title
}

/**
 * Image Task Detail - NEW API
 * Response from view-engagement API (engagement_page=1)
 * Updated to match new /v1/engagement/view-engagement endpoint
 */
export interface ImageTaskDetail {
  task_id: number;
  task_title: string; // Internal task title
  engagement_title: string; // User-facing title
  engagement_type_id: number;
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  reward_type: number; // 1 = Variable, 2 = Fixed points
  points?: number; // Points for fixed reward type (same as full_month_points)
  full_month_points?: number; // Alternative field name for points
  audit_criteria: string; // CKEditor HTML content
  instructions: string; // NEW: Detailed task instructions
  description: string; // NEW: Task description
  failure_msg: string; // NEW: Failure message
  template_instructions: string; // JSON string of ImageTaskTemplateInstructions
  whitelist_type: number; // 1=Excel, 2=Zone, 3=Enrollment
  zone_type?: number; // 0=All zones, 1=Specific zone
  zone_id?: number[]; // Array of zone IDs
  role_type: number; // Target user role
  is_whitelisted?: boolean; // If users already whitelisted
  user_group_id?: number; // For enrollment-based whitelist
  enrollment_setting_id?: number; // For enrollment-based whitelist
}

/**
 * Sub Task (for customized reward type)
 */
export interface SubTask {
  id: number; // sub_task_id
  task_id: number;
  start_date: string;
  end_date: string;
}

/**
 * Request: Create/Update Image Engagement (Picture Task) - NEW API
 * Updated to match /v1/engagement/create-image-task endpoint
 */
export interface CreateImageEngagementRequest {
  task_title: string; // Internal task title
  engagement_page: number; // Must be 1 for picture tasks
  engagement_type_id: string | number; // Engagement type ID from engagementtypes API
  engagement_title: string; // User-facing title
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  reward_type: string | number; // 1 = Variable, 2 = Fixed points
  instruction_image_ids: number[]; // Array of uploaded instruction image IDs
  points: number; // Points awarded for completion (integer only)
  audit_criteria: string; // CKEditor HTML content for audit/verification
  instructions: string; // Detailed task instructions
  description: string; // Task description
  failure_msg: string; // Message shown when task fails
  template_instructions: ImageTaskTemplateInstructions; // JSON object with template data
  is_edit: number; // 0 = Create new, 1 = Update existing
  task_id?: string | number; // Required if is_edit = 1
  whitelist_type: string | number; // 1=Excel, 2=Zone-based, 3=Enrollment
  zone_type?: string | number; // 1=Region, 2=State, 3=City (required if whitelist_type=2)
  zone_id?: number[]; // Array of zone IDs for multiple zone selection (conditional)
  role_type: string | number; // Target user role
  program_id: string | number; // Program ID
  user_group_id?: string | number; // User group ID (required if whitelist_type=3)
  enrollment_setting_id?: string | number; // Enrollment setting ID (required if whitelist_type=3)
}

/**
 * Response: Create Image Engagement
 * API returns: { message, task_id, engagement_type_id }
 */
export interface CreateImageEngagementResponse {
  message: string;
  task_id: number;
  engagement_type_id: number;
}

/**
 * Request: Upload Instruction Image
 * Uploads image and returns URL
 */
export interface UploadInstructionImageRequest {
  engagement_type_id: number; // ID from engagement types API
  element_id: number; // Same as engagement_type_id
  image_type: number; // 1 = right, 2 = wrong, 3 = single
  image: string; // Base64 encoded image
}

/**
 * Response: Upload Instruction Image
 */
export interface UploadInstructionImageResponse {
  body: {
    data: {
      image: string; // URL of uploaded image
    };
  };
  status: number;
}

/**
 * Response: Excel Upload Whitelist
 */
export interface ExcelUploadWhitelistResponse {
  body: {
    data: {
      success_count: number;
      error_count: number;
      errors: Array<{
        mobile_number: string;
        reason: string;
      }>;
      message?: string;
    };
  };
  status: number;
}

/**
 * Picture-task submission (engagement approval list row)
 */
export interface EngagementTaskSubmission {
  submission_id: number;
  task_id: number;
  task_name: string;
  user_id: number;
  user_name: string;
  mobile_number: string;
  submitted_images: string[];
  submission_date: string;
  status: string;
  /** 1 = Pending, 2 = Approved, 3 = Rejected */
  status_code: 1 | 2 | 3;
  points: number;
  outlet_code?: string | null;
  sub_task_id?: number | null;
  approved_by?: number | null;
  approved_date?: string | null;
  rejected_by?: number | null;
  rejected_date?: string | null;
  rejection_reason?: string | null;
}

export interface EngagementTaskListCounts {
  total_records: number;
  pending_records: number;
  approved_records: number;
  rejected_records: number;
}

export interface ListEngagementTasksRequest {
  status?: number;
  task_id?: number;
  user_id?: number;
  mobile_number?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
  /** Free-text search mapped to mobile_number when numeric-looking, else ignored server-side helpers */
  key_word?: string;
}

export interface ListEngagementTasksResponse {
  success?: boolean;
  status?: number;
  counts?: EngagementTaskListCounts;
  data?:
    | EngagementTaskSubmission[]
    | {
        counts?: EngagementTaskListCounts;
        data?: EngagementTaskSubmission[];
      };
  message?: string;
}

/**
 * Approve/reject picture-task submission
 * action: 1 = approve, 2 = reject
 */
export interface EngagementTaskActionRequest {
  task_submission_id: number;
  action: 1 | 2;
  remarks?: string;
}

export interface EngagementTaskActionResponse {
  message?: string;
  submission_id?: number;
  user_id?: number;
  points_credited?: number;
  status?: string;
  data?: {
    message?: string;
  };
}
